import { create } from 'zustand';
import { getState, useState } from '@/framework';
import type { IExportState, StateType } from '@/version-types';
import { getBuiltinContextMenuItems } from '../builtin';
import { GetCustomContextMenuFn, IExportContextMenuItem } from '../type';

/** 状态 */
interface IState {
    /** 自定义右键菜单 */
    getCustomContextMenu: GetCustomContextMenuFn;
    /** 是否禁用右键菜单 */
    disableMenu: boolean;
    /** 隐藏右键菜单类型 */
    hiddenContextMenuTypes: Array<string>;
    /** 菜单距离边缘的间距 */
    menuSpace: number;
    /** 箭头显示左边的间距 */
    arrowMenuInLeftSpace: number;
    /** 默认右键菜单列表 */
    contextMenuList: Array<IExportContextMenuItem>;
}

/** 动作 */
interface IAction {
    /** 设置自定义右键菜单 */
    setCustomContextMenuFn: (customContextMenuFn: GetCustomContextMenuFn) => void;
    /** 设置是否禁用右键菜单 */
    setDisableMenu: (disableMenu: boolean) => void;
    /** 设置隐藏右键菜单类型 */
    setHiddenContextMenuTypes: (hiddenContextMenuTypes: Array<string>) => void;
    /** 设置菜单间距 */
    setMenuSpace: (menuSpace: number) => void;
    /** 设置箭头显示左边的间距 */
    setArrowMenuInLeftSpace: (arrowMenuInLeftSpace: number) => void;
    /** 追加右键菜单项 */
    addContextMenu: (defaultContextMenuList: Array<IExportContextMenuItem>) => void;
    /** 全量替换已注册的右键菜单项 */
    setContextMenuList: (contextMenuList: Array<IExportContextMenuItem>) => void;
}

/** 存储类型 */
interface IStore extends IState, IAction {}

/** 创建存储 */
const createState = (_: string) => {
    const store = create<IStore>((set, get) => ({
        getCustomContextMenu: () => null,
        setCustomContextMenuFn(getCustomContextMenu) {
            set({ getCustomContextMenu });
        },

        disableMenu: false,
        setDisableMenu(disableMenu) {
            set({ disableMenu });
        },

        hiddenContextMenuTypes: [],
        setHiddenContextMenuTypes(hiddenContextMenuTypes) {
            set({ hiddenContextMenuTypes });
        },

        menuSpace: 10,
        setMenuSpace(menuSpace) {
            set({ menuSpace });
        },

        arrowMenuInLeftSpace: 10,
        setArrowMenuInLeftSpace(arrowMenuInLeftSpace) {
            set({ arrowMenuInLeftSpace });
        },

        contextMenuList: getBuiltinContextMenuItems(),
        addContextMenu(contextMenuList) {
            set({ contextMenuList: [...get().contextMenuList, ...contextMenuList] });
        },
        setContextMenuList(contextMenuList) {
            set({ contextMenuList });
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'context-menu-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useContextMenuState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getContextMenuState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
